import React from 'react';

import { Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import GradientCard from 'components/RabbitOKRCreationPanel/components/GradientCard';
import { SelectAndSearchComponent } from 'components/RabbitOKRCreationPanel/components/SelectAndSearchComponent';
import { InputMaterialUITextArea } from 'components/RabbitOKRSidePanel/Components/TextAreaMaterialUI';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { themeColors } from 'themes/clients/baseTheme';

import { OKR_TYPES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { sendAIButtonClickedEvent, sendMeshMavenClickedEvent } from 'constants/mixpanelHelper';
import { GOALS_EVENT_SOURCES } from 'constants/trackingEvents';

import { Button } from 'uiToolkitV1/Button';
import GradientButton from 'uiToolkitV1/GradientButton/GradientButton';
import { TextLabel } from 'uiToolkitV1/Typography';

export const additionalInformationOptions = [
  {
    tagName: 'Title',
    value: 'title',
    subtitle: 'The title of the goal',
    isSelected: true,
    isMandatory: true,
  },
  {
    tagName: 'Role',
    subtitle: 'The roles of owners and collaborators associated with the goal',
    isSelected: true,
    value: 'role',
  },
  {
    tagName: 'Function',
    subtitle: 'The functions defined for the owners and collaborators associated with the goal',
    isSelected: true,
    value: 'function',
  },
  {
    tagName: 'Alignment',
    subtitle: 'The alignment of the goal with its parent goal tree.',
    isSelected: true,
    value: 'alignment',
    isMandatory: true,
  },
  {
    tagName: 'Timeline',
    subtitle: `The timeline defined for the `,
    isSelected: true,
    value: 'timeline',
  },
  {
    tagName: 'Objective Level',
    subtitle: '',
    isSelected: true,
    value: 'objectiveLevel',
  },
  {
    tagName: 'Description',
    subtitle: 'The description added for the goal',
    isSelected: true,
    value: 'description',
  },
];

export const StyledInputMaterialUITextArea = styled(InputMaterialUITextArea)`
  border-bottom: 1px ${(props) => (props.isError ? 'solid #c62c02' : `dashed ${props.theme.colors.text.g3}`)};
  background: transparent;
`;

export const AdvancedOptionsScreen = React.forwardRef(
  (
    {
      advanceSelectedOptions,
      setAdvanceSelectedOptions,
      advanceGenerationClicked,
      formattedAdditionalInformationOptions = [],
      userPromptText,
      setUserPromptText,
      handleGenerateButtonClick,
      children,
      isAdvanceResponseGenerating,
      okrDetails,
      isNewOKR,
      isMetricGeneration = false,
      isShowCancelButton = false,
      onCancel = () => {},
    },
    ref
  ) => {
    const handleAdditionOptionSelection = (selectedOptions) => {
      setAdvanceSelectedOptions(selectedOptions);
    };

    const { moduleName } = useOrganisationSettings();

    return (
      <Flex direction="column" px="8px" flex={1} height="calc(100% - 133px)" gap="12px" ref={ref}>
        <GradientCard title="Personalize your results">
          <Flex direction="column">
            <TextLabel color={themeColors.text.g2}>Provide detailed instructions to Maven in the space below</TextLabel>

            <StyledInputMaterialUITextArea
              value={userPromptText}
              onChange={(event) => setUserPromptText(event.target.value)}
              placeholder="Include instructions, such as “Make the title more detailed”"
            />

            <TextLabel color={themeColors.text.g2} mt="10px">
              Select the characteristics of the {modifyString(moduleName, StringModifier.SENTENCE_CASE)} that you can
              share with Maven to generate better results
            </TextLabel>
            <SelectAndSearchComponent
              tags={formattedAdditionalInformationOptions}
              curSelectedTags={advanceSelectedOptions}
              onChange={(tags) => handleAdditionOptionSelection(tags)}
              popupPlacement="bottom"
            />
            <Flex justifyContent={isShowCancelButton ? 'space-between' : 'flex-end'} pt="8px">
              {isShowCancelButton && (
                <Button
                  variant="tertiary"
                  colorScheme="blue"
                  onClick={() => {
                    onCancel();
                  }}
                  isDisabled={isAdvanceResponseGenerating}
                >
                  Cancel
                </Button>
              )}
              <GradientButton
                text="Generate"
                onClick={() => {
                  sendAIButtonClickedEvent({
                    eventProperties: {
                      source: okrDetails?.uuid
                        ? GOALS_EVENT_SOURCES.EDIT_GOAL_PANEL
                        : GOALS_EVENT_SOURCES.ADD_GOAL_PANEL,
                      type: GOALS_EVENT_SOURCES.AI.type.GENERATE_AFTER_CONFIGURATION,
                      goalId: okrDetails?.uuid || 'NA',
                      purpose: okrDetails?.uuid
                        ? GOALS_EVENT_SOURCES.AI.purpose.Goal_Editing
                        : GOALS_EVENT_SOURCES.AI.purpose.Goal_Creation,
                    },
                  });

                  handleGenerateButtonClick();
                  sendMeshMavenClickedEvent({
                    eventProperties: {
                      goalId: okrDetails?.uuid || null,
                      page:
                        OKR_TYPES.OBJECTIVE === okrDetails?.type
                          ? `${isNewOKR ? 'Add' : 'Edit'} Objective Panel`
                          : `${isNewOKR ? 'Add' : 'Edit'} KR Panel`,
                      purpose: isMetricGeneration ? 'Goal Tracking' : 'Goal Title',
                      source:
                        OKR_TYPES.OBJECTIVE === okrDetails?.type
                          ? `${isNewOKR ? 'Add' : 'Edit'} Objective Panel`
                          : `${isNewOKR ? 'Add' : 'Edit'} KR Panel`,
                      type: 'YES',
                    },
                  });
                }}
                isDisabled={isAdvanceResponseGenerating}
              />
            </Flex>
          </Flex>
        </GradientCard>

        {advanceGenerationClicked && children && React.cloneElement(children)}
      </Flex>
    );
  }
);
